/**
 * Copyright (c) 2025 Kalpit Patel
 *
 * Sole Developer and Owner:
 * Kalpit Patel is the sole developer and creator of this entire application.
 * No other individual or entity has contributed to the development of this software.
 * All rights reserved.
 *
 * This file is part of AksharJobs application.
 */

import React, { useEffect, useRef, useCallback } from 'react';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import { createLeafletMap, isLeafletMapUsable, safeLeafletSetView } from '../utils/safeLeaflet';

// Fix for default marker icons in Leaflet with webpack
delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: require('leaflet/dist/images/marker-icon-2x.png'),
  iconUrl: require('leaflet/dist/images/marker-icon.png'),
  shadowUrl: require('leaflet/dist/images/marker-shadow.png'),
});

const LocationMap = ({
  latitude,
  longitude,
  onLocationChange,
  onAddressChange,
  autoUpdateAddress: autoUpdateAddressProp = false,
}) => {
  const mapRef = useRef(null);
  const mapInstanceRef = useRef(null);
  const markerRef = useRef(null);
  const searchInputRef = useRef(null);
  const isMountedRef = useRef(true);
  const initAttemptRef = useRef(0);
  const onLocationChangeRef = useRef(onLocationChange);
  const onAddressChangeRef = useRef(onAddressChange);
  const autoUpdateAddressRef = useRef(autoUpdateAddressProp);
  const placeMarkerRef = useRef(null);

  useEffect(() => {
    onLocationChangeRef.current = onLocationChange;
  }, [onLocationChange]);

  useEffect(() => {
    onAddressChangeRef.current = onAddressChange;
  }, [onAddressChange]);

  useEffect(() => {
    autoUpdateAddressRef.current = autoUpdateAddressProp;
  }, [autoUpdateAddressProp]);

  const reverseGeocode = useCallback((lat, lng) => {
    if (!autoUpdateAddressRef.current) return;

    fetch(`https://nominatim.openstreetmap.org/reverse?format=json&lat=${lat}&lon=${lng}`)
      .then((response) => response.json())
      .then((data) => {
        if (data.display_name && onAddressChangeRef.current) {
          onAddressChangeRef.current(data.display_name);
        }
      })
      .catch((error) => console.log('Geocoding error:', error));
  }, []);

  const placeMarker = useCallback((lat, lng, { updateAddress = true, notifyParent = true } = {}) => {
    const map = mapInstanceRef.current;
    if (!isLeafletMapUsable(map) || !isMountedRef.current) return;

    const latLng = L.latLng(lat, lng);

    if (markerRef.current) {
      markerRef.current.setLatLng(latLng);
    } else {
      const marker = L.marker(latLng, { draggable: true }).addTo(map);
      marker.on('dragend', () => {
        const position = marker.getLatLng();
        if (onLocationChangeRef.current) {
          onLocationChangeRef.current(position.lat, position.lng);
        }
        if (autoUpdateAddressRef.current) {
          reverseGeocode(position.lat, position.lng);
        }
      });
      markerRef.current = marker;
    }

    if (notifyParent && onLocationChangeRef.current) {
      onLocationChangeRef.current(lat, lng);
    }

    if (updateAddress) {
      reverseGeocode(lat, lng);
    }
  }, [reverseGeocode]);

  placeMarkerRef.current = placeMarker;

  const initializeMap = useCallback(() => {
    if (!isMountedRef.current || mapInstanceRef.current || !mapRef.current) {
      return false;
    }

    const initialLat = parseFloat(latitude) || -1.286389;
    const initialLng = parseFloat(longitude) || 36.817223;

    const map = createLeafletMap(
      mapRef.current,
      [initialLat, initialLng],
      12,
      { L }
    );

    if (!map) {
      if (initAttemptRef.current < 10) {
        initAttemptRef.current += 1;
        window.setTimeout(initializeMap, 200);
      }
      return false;
    }

    initAttemptRef.current = 0;
    mapInstanceRef.current = map;

    L.tileLayer('https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png', {
      attribution: '© OpenStreetMap contributors',
      maxZoom: 19,
    }).addTo(map);

    map.on('click', (e) => {
      placeMarkerRef.current?.(e.latlng.lat, e.latlng.lng);
    });

    map.whenReady(() => {
      if (!isMountedRef.current || !isLeafletMapUsable(map)) return;
      map.invalidateSize({ animate: false, pan: false });

      if (latitude && longitude) {
        placeMarkerRef.current?.(latitude, longitude, { updateAddress: false, notifyParent: false });
      } else if (navigator.geolocation) {
        navigator.geolocation.getCurrentPosition(
          (position) => {
            if (!isMountedRef.current || !isLeafletMapUsable(mapInstanceRef.current)) return;
            const lat = position.coords.latitude;
            const lng = position.coords.longitude;
            safeLeafletSetView(mapInstanceRef.current, [lat, lng], 13);
            placeMarkerRef.current?.(lat, lng, { updateAddress: false, notifyParent: true });
          },
          () => {}
        );
      }
    });

    return true;
  }, []);

  // Initialize map once on mount — never destroy/recreate on coordinate changes
  useEffect(() => {
    isMountedRef.current = true;
    initAttemptRef.current = 0;
    initializeMap();

    return () => {
      isMountedRef.current = false;
      initAttemptRef.current = 0;

      if (markerRef.current && mapInstanceRef.current) {
        try {
          mapInstanceRef.current.removeLayer(markerRef.current);
        } catch (err) {
          console.warn('Marker cleanup skipped:', err);
        }
        markerRef.current = null;
      }

      if (mapInstanceRef.current) {
        try {
          mapInstanceRef.current.off();
          mapInstanceRef.current.remove();
        } catch (err) {
          console.warn('Map cleanup skipped:', err);
        }
        mapInstanceRef.current = null;
      }
    };
  }, [initializeMap]);

  // Sync external coordinate changes without destroying the map
  useEffect(() => {
    const map = mapInstanceRef.current;
    if (!isLeafletMapUsable(map) || latitude == null || longitude == null || latitude === '' || longitude === '') {
      return;
    }

    const lat = parseFloat(latitude);
    const lng = parseFloat(longitude);
    if (Number.isNaN(lat) || Number.isNaN(lng)) return;

    const current = markerRef.current?.getLatLng();
    if (current && Math.abs(current.lat - lat) < 0.000001 && Math.abs(current.lng - lng) < 0.000001) {
      return;
    }

    if (markerRef.current) {
      markerRef.current.setLatLng([lat, lng]);
    } else {
      placeMarker(lat, lng, { updateAddress: false, notifyParent: false });
    }
  }, [latitude, longitude, placeMarker]);

  const searchLocation = (query) => {
    if (!query) return;

    fetch(`https://nominatim.openstreetmap.org/search?format=json&q=${encodeURIComponent(query)}`)
      .then((response) => response.json())
      .then((data) => {
        if (data && data.length > 0) {
          const lat = parseFloat(data[0].lat);
          const lng = parseFloat(data[0].lon);
          if (isLeafletMapUsable(mapInstanceRef.current)) {
            safeLeafletSetView(mapInstanceRef.current, [lat, lng], 13);
            placeMarkerRef.current?.(lat, lng, { updateAddress: true, notifyParent: true });
          }
        } else {
          alert('Location not found. Please try a different search term.');
        }
      })
      .catch((error) => {
        console.log('Search error:', error);
        alert('Error searching location. Please try again.');
      });
  };

  const handleSearchKeyPress = (e) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      searchLocation(searchInputRef.current.value);
    }
  };

  return (
    <div className="location-map-container">
      <div className="map-search-container">
        <input
          ref={searchInputRef}
          type="text"
          placeholder="Search for a location..."
          onKeyPress={handleSearchKeyPress}
          className="map-search-input"
        />
        <button
          type="button"
          onClick={() => searchLocation(searchInputRef.current.value)}
          className="map-search-button"
        >
          Search
        </button>
      </div>

      <div
        ref={mapRef}
        className="leaflet-map"
        style={{
          width: '100%',
          height: '400px',
          borderRadius: '8px',
          border: '2px solid #e0e0e0',
          marginTop: '10px',
        }}
      />

      <div className="map-coordinates-display">
        <strong>Selected Coordinates:</strong>{' '}
        {latitude && longitude
          ? `Lat: ${parseFloat(latitude).toFixed(6)}, Lng: ${parseFloat(longitude).toFixed(6)}`
          : 'Click on the map to select a location'}
      </div>
    </div>
  );
};

export default LocationMap;
